<script>
    import { onDestroy, onMount } from 'svelte';
    import { planStore } from "../views/store/plan-store";
    import { PRConnect } from "@projectroom/ui-lib";
    import { _ } from 'svelte-i18n';
    import Payment from './Payment.svelte';
    import { Dialog, Card, Button } from "../lib/ui";

    let authType;
    let authSelectFlag;
    let isAvailableFlag;
    let minimumAuthType;

    let planStoreSubsc;
    let paymentComponent;

    onMount(() => {
        planStoreSubsc = planStore.subscribe(message => {
            authType = message?.authType;
            authSelectFlag = message?.authSelectFlag;
            isAvailableFlag = message?.isAvailableFlag;
            minimumAuthType = message?.minimumAuthType;
        });
    });

    onDestroy(() => {
        planStoreSubsc && planStoreSubsc();
    });

    // ── 요금제 정의 (i18n 키 참조 유지) ──────────────────────────────
    // ALL-IN-ONE은 제품에서 제외(안 쓰기로 함). FREE / STARTER / PRO / ENTERPRISE만 노출.
    // items: {section} = 소제목, {a,b} = 체크 항목(a=강조, b=설명, b 없으면 a만).
    const plans = [
        {
            id: "FREE",
            name: "project.plan.3",
            desc: ["project.plan.5", "project.plan.6"],
            priceMain: "project.plan.8",
            priceSuffix: "project.plan.9",
            heading: "project.plan.10",
            items: [
                { a: "project.plan.11", b: "project.plan.12" },
                { a: "project.plan.13", b: "project.plan.14" },
                { a: "project.plan.15", b: "project.plan.16" },
                { a: "project.plan.17", b: "project.plan.18" },
                { a: "project.plan.19", b: "project.plan.20" },
            ],
            recommended: false,
            cta: { currentKey: "project.plan.28" },
        },
        {
            id: "STARTER",
            name: "project.plan.30",
            desc: ["project.plan.31", "project.plan.32"],
            priceMain: "project.plan.35",
            priceSuffix: "project.plan.36",
            heading: "project.plan.37",
            items: [
                { section: "project.plan.section_mini" },
                { a: "project.plan.40", b: "project.plan.41" },
                { a: "project.plan.42", b: "project.plan.43" },
                { a: "project.plan.44", b: "project.plan.45" },
                { a: "project.plan.46", b: "project.plan.47" },
                { a: "project.plan.48", b: "project.plan.49" },
                { a: "project.plan.50", b: "project.plan.51" },
                { a: "project.plan.52", b: "project.plan.53" },
                { section: "project.plan.section_ai" },
                { a: "project.plan.54", b: "project.plan.55" },
                { a: "project.plan.56", b: "project.plan.57" },
                { a: "project.plan.58", b: "project.plan.59" },
            ],
            recommended: false,
            cta: { currentKey: "project.plan.60", selectKey: "project.plan.61", from: ["FREE"], type: "pay" },
        },
        {
            id: "PRO",
            name: "project.plan.62",
            desc: ["project.plan.63", "project.plan.64"],
            priceMain: "project.plan.68",
            priceSuffix: "project.plan.69",
            heading: "project.plan.70",
            badge: "project.plan.pro_badge",
            items: [
                { section: "project.plan.section_mini" },
                { a: "project.plan.73", b: "project.plan.74" },
                { a: "project.plan.80", b: "project.plan.81" },
                { a: "project.plan.83", b: "project.plan.84" },
                { a: "project.plan.86", b: "project.plan.87" },
                { a: "project.plan.88", b: "project.plan.89" },
                { section: "project.plan.section_ai" },
                { a: "project.plan.90", b: "project.plan.91" },
                { a: "project.plan.92", b: "project.plan.93" },
                { a: "project.plan.94", b: "project.plan.95" },
            ],
            recommended: true,
            cta: { currentKey: "project.plan.pro_current", selectKey: "project.plan.pro_select", from: ["FREE", "STARTER"], type: "pay" },
        },
        {
            id: "ENTERPRISE",
            name: "project.plan.ent_name",
            desc: ["project.plan.ent_desc"],
            priceMain: "project.plan.ent_price",
            priceSuffix: "",
            heading: "",
            items: [
                { a: "project.plan.ent_f1" },
                { a: "project.plan.ent_f2" },
                { a: "project.plan.ent_f3" },
            ],
            recommended: false,
            cta: { currentKey: "project.plan.ent_current", selectKey: "project.plan.ent_contact", type: "consult" },
        },
    ];

    // 추천(PRO) 카드는 기존 ALL-IN-ONE의 분홍 강조 디자인을 이어받는다.
    // (shadcn primary 토큰은 인디고 #6372ff 라, 브랜드 분홍은 직접 지정)
    const PINK = "#e55e8a";

    // 티어별 액센트 색 (아이콘 박스 + 현재플랜 테두리).
    // 채도 있는 색은 분홍(PRO) 하나만 — 나머지는 슬레이트 램프(밝→어둠)로 두어 분홍과 충돌하지 않게.
    const ACCENTS = {
        FREE: "#94a3b8",       // slate-400
        STARTER: "#64748b",    // slate-500
        PRO: PINK,             // brand pink (유일한 채도 액센트)
        ENTERPRISE: "#475569", // slate-600
    };

    // 티어별 아이콘 (Lucide 스타일 라인 SVG, currentColor 상속). 선물/번개/왕관/빌딩.
    const svg = (inner) =>
        `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="22" height="22">${inner}</svg>`;
    const ICONS = {
        FREE: svg('<rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/>'),
        STARTER: svg('<path d="M13 2 3 14h9l-1 8 10-12h-9z"/>'),
        PRO: svg('<path d="M2 18h20"/><path d="M4 18l-2-9 6 4 4-8 4 8 6-4-2 9"/>'),
        ENTERPRISE: svg('<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2M10 6h4M10 10h4M10 14h4"/>'),
    };

    const cardStyle = (plan, currentAuthType) => {
        if (plan.recommended) {
            return `border:2px solid ${PINK};` +
                "background-image:linear-gradient(180deg, rgba(229,94,138,0.12) 0%, rgba(229,94,138,0) 45%);" +
                "box-shadow:0 8px 24px -6px rgba(229,94,138,0.25);";
        }
        if (currentAuthType === plan.id) {
            return `border:2px solid ${ACCENTS[plan.id]};`;
        }
        // 기본 테두리: --sb-border(90% 밝기)는 흰-on-흰으로 안 보여서 foreground 14%로 대비 확보(라이트=옅은 검정, 다크=옅은 흰색).
        return "border:1px solid hsl(var(--sb-foreground) / 0.14);";
    };

    // 결제 팝업 오픈: 선택한 요금제 가격 조회 후 Payment 팝업 표시
    const selectPlan = (selectedAuthType) => {
        const currency = Intl.DateTimeFormat().resolvedOptions().timeZone === "Asia/Seoul" ? "KRW" : "USD";
        PRConnect.send("/api/Payment/getPlanPriceInfo", { authType: selectedAuthType, currency }, data => {
            if (data.header.status === "success") {
                paymentComponent.showPopup(data.result.amount, data.result.authType, true, currency);
            }
        });
    };

    const handleConsulting = () => {
        planStore.setAuthSelectFlag(false);
        // Plan은 이제 빌더 등 여러 화면에서 렌더되므로 ChannelIO 미로드 컨텍스트를 대비해 가드.
        if (typeof ChannelIO === "function") ChannelIO('openChat');
    };

    const clickPlanPopupClose = () => planStore.setAuthSelectFlag(false);
    const clickChangePlan = () => planStore.setAuthSelectFlagAndAvailableFlag(true, false);
    const clickAvailablePopupClose = () => planStore.setIsAvailableFlag(false);
</script>

<!-- 요금제 선택 팝업 (shadcn Dialog) -->
{#if authType}
    <Dialog open={authSelectFlag} on:close={clickPlanPopupClose} class="tw-max-w-6xl">
        <svelte:fragment slot="header">
            <div class="tw-flex tw-items-center tw-gap-2.5">
                <img src="/static/images/avatalk-charecter-pink.svg" loading="lazy" class="tw-h-7 tw-w-7" alt="" />
                <div>
                    <h2 class="tw-m-0 tw-text-lg tw-font-semibold tw-text-foreground">
                        {$_('project.plan.1')}<span style="color:{PINK}">{$_('project.plan.2')}</span>
                    </h2>
                </div>
            </div>
        </svelte:fragment>

        <div class="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
            {#each plans as plan}
                <Card
                    class="tw-relative tw-flex tw-flex-col tw-p-5 tw-shadow-md tw-transition-shadow tw-duration-200 hover:tw-shadow-xl"
                    style={cardStyle(plan, authType)}
                >
                    {#if plan.badge}
                        <span
                            class="tw-absolute tw-right-3 tw-top-3 tw-inline-flex tw-items-center tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-white"
                            style="background:{PINK}"
                        >{$_(plan.badge)}</span>
                    {/if}

                    <!-- 상단: 아이콘 / 이름 / 설명 / 가격 -->
                    <div class="tw-mb-4">
                        <div
                            class="tw-mb-3 tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-xl"
                            style="background:{ACCENTS[plan.id]}1a;color:{ACCENTS[plan.id]}"
                        >{@html ICONS[plan.id]}</div>
                        <div class="tw-text-xl tw-font-bold tw-text-foreground">{$_(plan.name)}</div>
                        <p class="tw-mb-0 tw-mt-1 tw-min-h-[2.5rem] tw-text-xs tw-leading-relaxed tw-text-muted-foreground">
                            {#each plan.desc as d, i}{$_(d)}{#if i < plan.desc.length - 1}<br />{/if}{/each}
                        </p>
                        <div class="tw-mt-2 tw-text-foreground">
                            <span class="tw-text-2xl tw-font-bold">{$_(plan.priceMain)}</span>{#if plan.priceSuffix}<span class="tw-text-sm tw-text-muted-foreground">{$_(plan.priceSuffix)}</span>{/if}
                        </div>
                    </div>

                    <!-- 기능 목록 -->
                    <div class="tw-flex-1 tw-space-y-1.5 tw-border-t tw-border-border tw-pt-4">
                        {#if plan.heading}
                            <div class="tw-mb-1 tw-text-xs tw-font-semibold tw-text-foreground">{$_(plan.heading)}</div>
                        {/if}
                        {#each plan.items as item}
                            {#if item.section}
                                <div class="tw-mt-3 tw-text-xs tw-font-bold" style="color:{PINK}">{$_(item.section)}</div>
                            {:else}
                                <div class="tw-flex tw-items-start tw-gap-1.5">
                                    <img src="/static/images/check-icon_1.svg" loading="lazy" alt="" class="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                                    <div class="tw-text-xs tw-leading-relaxed tw-text-foreground">
                                        <strong class="tw-font-semibold">{$_(item.a)}</strong>{#if item.b}<span class="tw-text-muted-foreground">{$_(item.b)}</span>{/if}
                                    </div>
                                </div>
                            {/if}
                        {/each}
                    </div>

                    <!-- CTA -->
                    <div class="tw-mt-5">
                        {#if authType === plan.id}
                            <Button variant="secondary" class="tw-w-full" disabled>{$_(plan.cta.currentKey)}</Button>
                        {:else if plan.cta.type === "consult"}
                            <Button variant="outline" class="tw-w-full" on:click={handleConsulting}>{$_(plan.cta.selectKey)}</Button>
                        {:else if plan.cta.from && plan.cta.from.includes(authType)}
                            <Button class="tw-w-full hover:tw-opacity-90" style="background:{PINK};color:#fff" on:click={() => selectPlan(plan.id)}>{$_(plan.cta.selectKey)}</Button>
                        {:else}
                            <!-- 다운그레이드 미지원: 카드 높이 정렬용 여백 -->
                            <div class="tw-h-10"></div>
                        {/if}
                    </div>
                </Card>
            {/each}
        </div>
    </Dialog>
{/if}

<!-- 권한 부족 안내 팝업 (특정 기능 접근 시) -->
<Dialog open={isAvailableFlag} title={$_('project.plan.117')} on:close={clickAvailablePopupClose}>
    <p class="tw-text-sm tw-text-foreground">
        {$_('project.plan.118')}<span class="tw-font-bold" style="color:{PINK}">{minimumAuthType}</span>{$_('project.plan.120')}
    </p>
    <svelte:fragment slot="footer">
        <Button variant="outline" on:click={clickAvailablePopupClose}>{$_('project.plan.122')}</Button>
        <Button class="hover:tw-opacity-90" style="background:{PINK};color:#fff" on:click={clickChangePlan}>{$_('project.plan.121')}</Button>
    </svelte:fragment>
</Dialog>

<Payment bind:this={paymentComponent} />
